import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Card, Col, Input, Modal, Row, Space, Table, Typography } from 'antd';
import isEmpty from 'lodash/isEmpty';
import { Search } from 'react-iconly';
import { RiAddLine, RiCloseFill } from 'react-icons/ri';
import { Filter } from 'iconsax-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import IntlMessages from '../../../../../../layout/components/lang/IntlMessages';
import basePagination from '../../../../../../const/pagination';
import urlPageLocalNotificationsEdit from '../../../../../../urls/urlPageLocalNotificationEdit';
import urlPageLocalNotificationCreate from '../../../../../../urls/urlPageLocalNotificationCreate';
import { getLocalNotificationsTableColumns } from './PageLocalNotifications.const';
import { HasRights, PrivateComponent } from '../../../../../../components/HasRights/HasRights';
import { Permissions } from '../../../../../../const/permissions';
import useToggle from '../../../../../../app/hooks/useToggle';
import useQueryParams from '../../../../../../app/hooks/useQueryParams';
import makeTableLoadingObject from '../../../../../../lib/makeTableLoadingObject';
import useElementSize from '../../../../../../app/hooks/useElementSize';
import handleResponse from '../../../../../../lib/handleResponse';
import applyFilter from '../../../../../../lib/applyFilter';
import resetFilter from '../../../../../../lib/resetFilter';
import BreadCrumbs from '../../../../../../layout/components/breadcrumbs';
import ActionButton from '../../../../../../layout/components/action-button';
import Sidebar from '../../../../../../components/Sidebar';
import DocumentationTooltip from '../../../../../../components/DocumentationTooltip/DocumentationTooltip';
import FormNotificationsFilter from '../../../../forms/FormNotificationsFilter';
import PreviewDictionaryTranslation from '../../../../../Translations/components/PreviewDictionaryTranslation/PreviewDictionaryTranslation';
import { useGetLanguagesQuery } from '../../../../../Translations/api/languagesApiSlice';
import {
  useDeleteLocalNotificationMutation,
  useGetLocalNotificationsQuery,
} from '../../../../api/localNotificationsApiSlice';



const { Title } = Typography;

const PageLocalNotifications = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [ searchQueryParams, setSearchParams ] = useSearchParams();
  const [ elementRef, size ] = useElementSize();
  const [ previewImage, setPreviewImage ] = useState('');
  const [ previewDictionaryId, setPreviewDictionaryId ] = useState({});
  const [ openDictionaryModal, toggleDictionaryModal ] = useToggle();
  const [ isVisibleFilterSidebar, toggleFilterSidebar ]  = useToggle();
  const [ previewModalOpen, togglePreviewModal ] = useToggle();

  const searchFields = [ 'title' ];
  const searchFilterFields = [ 'id', 'title', 'is_published', 'template' ];

  const {
    pagination,
    search,
    setSearchTerm,
    searchParams,
    initFilterValues,
    setInitFilterValues,
    handleChangeTableParams,
    resetIsManuallyTriggered,
    setResetManuallyTriggered,
  } = useQueryParams({ searchFields, searchFilterFields });

  const searchParamsString = searchParams.toString();
  const includes = `${isEmpty(searchParamsString) ? '?' : '&'}include=dynamic_data`;

  const { data: {
    data: localNotifications = [],
    pagination: localNotificationsPagination = {},
  } = { data: [], pagination: [] },
  isFetching,
  error: formErrors = {},
  } = useGetLocalNotificationsQuery({ queryParams: `${searchParamsString}`, includes }, { skip: !searchParamsString });

  const { data: {
    data: languages = [],
  } = { data: [] } } = useGetLanguagesQuery({ queryParams: 'limit=0' });
  const [ deleteLocalNotification, { isLoading: isLocalNotificationDeleting } ] = useDeleteLocalNotificationMutation();


  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setResetManuallyTriggered(false);
  };

  const canViewTemplateColumn = HasRights([ Permissions.CONSTRUCTORS.TEMPLATES.FILTER ]);

  const handlePreviewDictionary = (dictionaryId) => {
    setPreviewDictionaryId(dictionaryId);
    toggleDictionaryModal();
  };

  const handleEdit = (id) => {
    navigate(urlPageLocalNotificationsEdit({ notificationId: id }));
  };

  const handleDelete = (id) => {
    deleteLocalNotification(id)
      .unwrap()
      .then(() => handleResponse('success', intl, 'ui-general-deleted-successfully'))
      .catch((error) => handleResponse('error', intl, 'ui-general-delete-failed', [], error));
  };

  const handleCreate = () => {
    navigate(urlPageLocalNotificationCreate());
  };

  const handleImageCardClick = (preview) => {
    setPreviewImage(preview);
    togglePreviewModal();
  };

  const handlePreviewModalClose = () => {
    togglePreviewModal();
  };

  return (
    <>
      <Modal
        width={500}
        title={intl.formatMessage({ id: 'content-image-preview-card-title' })}
        centered
        visible={previewModalOpen}
        onCancel={handlePreviewModalClose}
        footer={false}
        closeIcon={<RiCloseFill className="remix-icon text-color-black-100" size={24} />}
      >
        <img alt="icon" src={previewImage} />
      </Modal>

      <Modal
        title={<IntlMessages id="dictionaries-translation-preview" />}
        width="60%"
        centered
        destroyOnClose
        visible={openDictionaryModal}
        onCancel={toggleDictionaryModal}
        footer={null}
        closeIcon={<RiCloseFill className="remix-icon text-color-black-100" size={24} />}
      >
        <PreviewDictionaryTranslation
          dictionaryId={previewDictionaryId}
          languages={languages}
        />
      </Modal>

      <Row
        gutter={[ 32, 32 ]}
        justify="space-between"
        className="hp-mb-32"
        align="middle"
      >
        <BreadCrumbs
          breadCrumbActive={
            <div>
              <IntlMessages id='local-notifications-breadcrumbs' />
              <DocumentationTooltip link="https://x-flow-ltd.atlassian.net/wiki/spaces/HCD/pages/1439039510/Local+notifications" />
            </div>
          }
        />

        <Col md={16} span={24}>
          <Row
            gutter={[ 32, 32 ]}
            justify="end"
            align="middle"
          >
            <Col sm={12} span={24}>
              <Input
                data-testid="search-input"
                allowClear={!isEmpty(search)}
                placeholder={intl.formatMessage({ id: 'ui-general-search-by-title' })}
                prefix={<Search set="curved" size={16} className="hp-text-color-black-80" />}
                value={search}
                onChange={handleSearch}
              />
            </Col>
            <Col>
              <Space>
                <PrivateComponent allowedPermissions={[ Permissions.OTHER.NOTIFICATIONS.LOCAL.CREATE ]}>
                  <ActionButton
                    title={<IntlMessages id='ui-general-create' />}
                    icon={<RiAddLine className="btn-icon-mr-1" />}
                    onClick={handleCreate}
                  />
                </PrivateComponent>

                <ActionButton
                  title=""
                  isActive={!isEmpty(initFilterValues)}
                  icon={<Filter size={18} data-testid="filter-action-icon" />}
                  onClick={toggleFilterSidebar}
                />
              </Space>
            </Col>
          </Row>
        </Col>

        <Sidebar
          title={<Title level={5}>{intl.formatMessage({ id: 'local-notifications-filter-title' })}</Title>}
          visible={isVisibleFilterSidebar}
          toggleSidebar={toggleFilterSidebar}
        >
          <FormNotificationsFilter
            initialValues={initFilterValues}
            formErrors={formErrors}
            isSubmitting={false}
            onCancel={() => {
              setResetManuallyTriggered(true);

              resetFilter({
                searchQueryParams,
                setSearchParams,
                setInitFilterValues,
                resetIsManuallyTriggered,
                setSearchTerm,
              });
            }}
            onSubmit={(values) => {
              applyFilter({
                values,
                searchQueryParams,
                searchFilterFields,
                setSearchParams,
                setSearchField: setSearchTerm,
                toggleFilterSidebar,
              });
            }}
          />
        </Sidebar>
      </Row>

      <Row gutter={[ 32, 32 ]}>
        <Col span={24}>
          <Card className="hp-border-color-black-40 hp-card-6">
            <div ref={elementRef}>
              <Table
                sticky
                scroll={{ x: 860 }}
                loading={makeTableLoadingObject(isFetching || isLocalNotificationDeleting)}
                rowKey="id"
                columns={getLocalNotificationsTableColumns(
                  handlePreviewDictionary,
                  handleEdit,
                  handleDelete,
                  handleImageCardClick,
                  size,
                  canViewTemplateColumn,
                )}
                dataSource={localNotifications}
                onChange={handleChangeTableParams}
                pagination={{
                  ...basePagination,
                  current: pagination.page,
                  pageSize: pagination.limit,
                  total: localNotificationsPagination?.total,
                }}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PageLocalNotifications;
